import { useCallback, useEffect, useMemo, useState } from 'react'
import { Outlet } from 'react-router-dom'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'

import { extendSession, getSession, logout } from 'services/api/authentication'
import {
  clearLocalSession,
  endSession,
  getHrAccessToken,
  getKnownHrExpiry,
  notifySessionExtended,
  setAuthenticationCookies,
  subscribeToSessionExtended,
} from 'services/session/authSession'

// Browsers cannot safely schedule a timeout longer than a signed 32-bit integer.
// Passport tokens currently live for six months, so long waits must be re-scheduled
// in bounded chunks instead of overflowing and firing immediately.
const MAX_TIMEOUT_DELAY = 2_147_000_000

const parseTime = (value) => {
  const parsed = value ? new Date(value).getTime() : Number.NaN
  return Number.isNaN(parsed) ? null : parsed
}

const formatCountdown = (milliseconds) => {
  const seconds = Math.max(0, Math.ceil(milliseconds / 1000))
  const minutesPart = String(Math.floor(seconds / 60)).padStart(2, '0')
  const secondsPart = String(seconds % 60).padStart(2, '0')
  return `${minutesPart}:${secondsPart}`
}

const SessionProvider = () => {
  const knownExpiry = parseTime(getKnownHrExpiry())
  const [expiresAt, setExpiresAt] = useState(knownExpiry)
  const [warningAt, setWarningAt] = useState(
    knownExpiry ? knownExpiry - 5 * 60 * 1000 : null
  )
  const [serverOffset, setServerOffset] = useState(0)
  const [clock, setClock] = useState(Date.now())
  const [isWarningOpen, setIsWarningOpen] = useState(false)
  const [isExtending, setIsExtending] = useState(false)
  const [extensionError, setExtensionError] = useState('')

  const applySessionData = useCallback((payload) => {
    const data = payload?.data || payload
    const nextExpiry = parseTime(data?.expires_at || data?.hr_access_token_expires_at)
    const nextWarning = parseTime(data?.warning_at)
    const serverTime = parseTime(data?.server_time)

    if (!nextExpiry) return

    const effectiveWarning = nextWarning || nextExpiry - 5 * 60 * 1000
    const authoritativeNow = serverTime || Date.now()

    setExpiresAt(nextExpiry)
    setWarningAt(effectiveWarning)
    setClock(Date.now())
    setIsWarningOpen(authoritativeNow >= effectiveWarning && authoritativeNow < nextExpiry)
    if (serverTime) setServerOffset(serverTime - Date.now())
  }, [])

  const refreshSession = useCallback(async () => {
    if (!getHrAccessToken()) return

    try {
      const response = await getSession()
      applySessionData(response)
    } catch (error) {
      // A 401 is handled centrally. Temporary failures do not end a known-valid session.
    }
  }, [applySessionData])

  useEffect(() => {
    refreshSession()
  }, [refreshSession])

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshSession()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [refreshSession])

  useEffect(() => {
    return subscribeToSessionExtended(({ expires_at: nextExpiry }) => {
      const parsedExpiry = parseTime(nextExpiry)
      if (!parsedExpiry) return

      setExpiresAt(parsedExpiry)
      setWarningAt(parsedExpiry - 5 * 60 * 1000)
      setExtensionError('')
      refreshSession()
    })
  }, [refreshSession])

  const expireSession = useCallback(() => {
    setIsWarningOpen(false)
    endSession({
      requestPath: '/session',
      authorizationPresent: Boolean(getHrAccessToken()),
      message: 'The authenticated session reached its server-reported expiry.',
    })
  }, [])

  useEffect(() => {
    if (!warningAt || !expiresAt) {
      setIsWarningOpen(false)
      return undefined
    }

    const currentServerTime = Date.now() + serverOffset
    if (currentServerTime >= expiresAt) {
      setIsWarningOpen(false)
      return undefined
    }

    if (currentServerTime >= warningAt) {
      setClock(Date.now())
      setIsWarningOpen(true)
      return undefined
    }

    setIsWarningOpen(false)
    let warningTimer

    const scheduleWarning = () => {
      const millisecondsUntilWarning = warningAt - (Date.now() + serverOffset)

      if (millisecondsUntilWarning <= 0) {
        setClock(Date.now())
        setIsWarningOpen(true)
        return
      }

      warningTimer = window.setTimeout(
        scheduleWarning,
        Math.min(millisecondsUntilWarning, MAX_TIMEOUT_DELAY)
      )
    }

    scheduleWarning()
    return () => window.clearTimeout(warningTimer)
  }, [expiresAt, serverOffset, warningAt])

  useEffect(() => {
    if (!expiresAt) return undefined

    let expiryTimer

    const scheduleExpiry = () => {
      const millisecondsUntilExpiry = expiresAt - (Date.now() + serverOffset)

      if (millisecondsUntilExpiry <= 0) {
        expireSession()
        return
      }

      expiryTimer = window.setTimeout(
        scheduleExpiry,
        Math.min(millisecondsUntilExpiry, MAX_TIMEOUT_DELAY)
      )
    }

    scheduleExpiry()
    return () => window.clearTimeout(expiryTimer)
  }, [expiresAt, expireSession, serverOffset])

  useEffect(() => {
    if (!isWarningOpen) return undefined

    setClock(Date.now())
    const countdownTimer = window.setInterval(() => setClock(Date.now()), 1000)
    return () => window.clearInterval(countdownTimer)
  }, [isWarningOpen])

  const serverNow = clock + serverOffset

  const countdown = useMemo(
    () => formatCountdown((expiresAt || serverNow) - serverNow),
    [expiresAt, serverNow]
  )

  const handleExtend = async () => {
    setIsExtending(true)
    setExtensionError('')

    try {
      const response = await extendSession()
      const data = response?.data || response

      setAuthenticationCookies({
        hrAccessToken: data.hr_access_token,
        hrExpiresAt: data.hr_access_token_expires_at || data.expires_at,
      })
      applySessionData(data)
      notifySessionExtended(data.hr_access_token_expires_at || data.expires_at)
    } catch (error) {
      if ((error?.status || error?.response?.status) === 401) {
        try {
          // Another tab may have rotated the shared cookie first. Verify the
          // current cookie before treating this expected race as a logout.
          const currentSession = await getSession()
          applySessionData(currentSession)
          return
        } catch (verificationError) {
          // A genuine 401 from GET /session is handled by the primary Axios
          // interceptor. Temporary verification failures keep the modal open.
        }
      }

      setExtensionError('Unable to extend your session. Please check your connection and try again.')
    } finally {
      setIsExtending(false)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      // Local logout must still complete if the API is unavailable.
    } finally {
      clearLocalSession({ reason: 'logout' })
      window.location.replace('/login')
    }
  }

  return (
    <>
      <Outlet />
      <Dialog
        open={isWarningOpen}
        disableEscapeKeyDown
        onClose={() => {}}
        aria-labelledby="session-expiry-title"
      >
        <DialogTitle id="session-expiry-title">Your session is about to expire.</DialogTitle>
        <DialogContent>
          <Typography>
            To protect your account, you will be signed out in {countdown}. Extend your session to
            continue working.
          </Typography>
          {extensionError && (
            <Typography color="error" mt={2}>
              {extensionError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogout} disabled={isExtending}>
            Logout
          </Button>
          <Button variant="contained" onClick={handleExtend} disabled={isExtending}>
            {isExtending ? 'Extending…' : 'Extend session'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default SessionProvider
